import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ImageGeneratorFeedbackReason } from "../../app/api/imageGenerator";
import { chipClassName } from "./ImageGeneratorControls";

const REASONS: Array<{ value: ImageGeneratorFeedbackReason; label: string }> = [
  { value: "incorrect", label: "Incorrect or incomplete" },
  { value: "blur", label: "Blur" },
  { value: "style", label: "Style or look" },
  { value: "other", label: "Other" },
];

type Props = {
  open: boolean;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    reason: ImageGeneratorFeedbackReason;
    details: string;
  }) => void;
};

const ImageGeneratorFeedbackModal: React.FC<Props> = ({
  open,
  submitting,
  onClose,
  onSubmit,
}) => {
  const [reason, setReason] = useState<ImageGeneratorFeedbackReason | "">("");
  const [details, setDetails] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }
    setReason("");
    setDetails("");
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) {
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, submitting]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center bg-[#1F1F1F]/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Share feedback"
      onClick={() => {
        if (!submitting) {
          onClose();
        }
      }}
    >
      <div
        className="w-full max-w-[520px] rounded-2xl bg-white p-5 shadow-2xl"
        onClick={event => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-secondary">Share feedback</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-secondary hover:bg-gray"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6l12 12M18 6 6 18"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <div className="mb-4 flex flex-wrap">
          {REASONS.map(option => (
            <button
              key={option.value}
              type="button"
              onClick={() => setReason(option.value)}
              className={`${chipClassName(reason === option.value)} mr-2 mb-2`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <textarea
          value={details}
          onChange={event => setDetails(event.target.value)}
          placeholder="Share details (optional)"
          maxLength={800}
          className="mb-4 min-h-[96px] w-full resize-y rounded-xl border border-[#737373] p-3 text-sm text-secondary focus:outline-none"
        />
        <div className="flex justify-end">
          <button
            type="button"
            disabled={!reason || submitting}
            onClick={() =>
              reason && onSubmit({ reason, details: details.trim() })
            }
            className="h-10 rounded-full bg-black px-8 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Submit
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageGeneratorFeedbackModal;
